import type { ErrorCode, FieldError } from './types.js';

/**
 * Connaro Core API client.
 *
 * Requests are same-origin: the dev server and the container's nginx both proxy
 * `/api` to Connaro Core. That keeps the session cookie a first-party cookie
 * and lets the browser attach the `Origin` header the CSRF check requires
 * (§56), without CORS in the middle.
 *
 * The Console never holds a credential of its own. Authentication is entirely
 * the HttpOnly session cookie (§11), which script cannot read — so there is
 * nothing here that reads or stores a token.
 */

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string | undefined;
  readonly fieldErrors: FieldError[];

  constructor(options: {
    code: ErrorCode;
    message: string;
    status: number;
    requestId?: string | undefined;
    fieldErrors?: FieldError[];
  }) {
    super(options.message);
    this.name = 'ApiError';
    this.code = options.code;
    this.status = options.status;
    this.requestId = options.requestId;
    this.fieldErrors = options.fieldErrors ?? [];
  }

  /** True when the user is simply not signed in, rather than not permitted. */
  get isUnauthenticated(): boolean {
    return this.code === 'UNAUTHENTICATED' || this.code === 'SESSION_EXPIRED';
  }

  /** Message for a specific form field, if the server named one. */
  fieldError(field: string): string | undefined {
    return this.fieldErrors.find((error) => error.field === field)?.message;
  }
}

type ErrorEnvelope = {
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
    details?: { fields?: FieldError[] };
  };
};

export type RequestOptions = {
  /* PUT is a full replacement, which Connaro Bot's command, timer and
     response-rule updates genuinely are: the operator posts the whole object
     and the server rebuilds its children from it. */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

const API_PREFIX = '/api/v1';

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method,
      // Same-origin by design; stated explicitly so a future origin change is a
      // deliberate edit rather than a silent behaviour change.
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (cause) {
    // Network-level failure: no response at all.
    throw new ApiError({
      code: 'DEPENDENCY_UNAVAILABLE',
      message: 'Could not reach Connaro. Check your connection and try again.',
      status: 0,
      ...(cause instanceof Error ? {} : {}),
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload: unknown = await response.json().catch(() => undefined);

  if (!response.ok) {
    const envelope = payload as ErrorEnvelope | undefined;
    throw new ApiError({
      code: (envelope?.error?.code ?? 'INTERNAL_ERROR') as ErrorCode,
      message: envelope?.error?.message ?? 'Something went wrong. Please try again.',
      status: response.status,
      requestId: envelope?.error?.requestId,
      fieldErrors: envelope?.error?.details?.fields ?? [],
    });
  }

  return payload as T;
}
